import { type Spec, createSpecStreamCompiler, defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { z } from "zod";

export { createSpecStreamCompiler, type Spec };
const id = z
  .string()
  .regex(/^[a-zA-Z][a-zA-Z0-9_-]{0,47}$/)
  .refine((value) => !["constructor", "prototype", "__proto__"].includes(value));
export const componentProps = {
  Stack: z.object({}).strict(),
  Panel: z.object({}).strict(),
  Heading: z.object({ text: z.string().min(1).max(120) }).strict(),
  Text: z.object({ text: z.string().min(1).max(900) }).strict(),
  Table: z
    .object({
      headers: z.array(z.string().min(1).max(60)).min(1).max(3),
      rows: z
        .array(z.array(z.string().max(100)).min(1).max(3))
        .min(1)
        .max(6),
    })
    .strict()
    .refine((value) => value.rows.every((row) => row.length === value.headers.length)),
  Reveal: z.object({ label: z.string().min(1).max(60), text: z.string().min(1).max(480) }).strict(),
  Activity: z.object({ activityId: id }).strict(),
  Exercise: z.object({ exerciseId: id }).strict(),
};
export const catalog = defineCatalog(schema, {
  components: {
    Stack: {
      props: componentProps.Stack,
      slots: ["default"],
      description: "Vertical teaching blocks",
    },
    Panel: {
      props: componentProps.Panel,
      slots: ["default"],
      description: "Grouped teaching blocks",
    },
    Heading: { props: componentProps.Heading, description: "Short board heading" },
    Text: { props: componentProps.Text, description: "Coherent explanation or reading paragraph" },
    Table: { props: componentProps.Table, description: "Small conjugation or comparison table" },
    Reveal: { props: componentProps.Reveal, description: "Optional extra help" },
    Activity: { props: componentProps.Activity, description: "Host-owned practice controls" },
    Exercise: {
      props: componentProps.Exercise,
      description: "Native first-party exercise controls",
    },
  },
  actions: {},
});
export function parseElement(value: unknown) {
  const element = z
    .object({
      type: z.enum([
        "Stack",
        "Panel",
        "Heading",
        "Text",
        "Table",
        "Reveal",
        "Activity",
        "Exercise",
      ]),
      props: z.record(z.string(), z.unknown()),
      children: z.array(id).max(12),
    })
    .strict()
    .parse(value);
  const props = componentProps[element.type].parse(element.props);
  if (!["Stack", "Panel"].includes(element.type) && element.children.length)
    throw new Error("Leaf children");
  return { ...element, props } as Spec["elements"][string];
}
